import { MotionValue, motion, useSpring, useTransform } from 'framer-motion';
import React, { useEffect } from 'react';

type PlaceValue = number | '.';

interface NumberProps {
    mv: MotionValue<number>;
    number: number;
    height: number;
    color?: string;
    className?: string; // Add className prop
}

function Number({ mv, number, height, color, className }: NumberProps) {
    const y = useTransform(mv, latest => {
        const placeValue = latest % 10;
        const offset = (10 + number - placeValue) % 10;
        let memo = offset * height;
        if (offset > 5) {
            memo -= 10 * height;
        }
        return memo;
    });

    return (
        <motion.span
            className={`absolute inset-0 flex items-center justify-center ${className || ''}`}
            style={{ y, color }}
        >
            {number}
        </motion.span>
    );
}

interface DigitProps {
    place: PlaceValue;
    value: number;
    height: number;
    digitStyle?: React.CSSProperties;
    color?: string;
    className?: string; // Add className prop
}

function Digit({ place, value, height, digitStyle, color, className }: DigitProps) {
    if (place === '.') {
        return (
            <span className={`relative w-[1ch] tabular-nums ${className || ''}`} style={{ height, ...digitStyle, width: 'fit-content' }}>
                .
            </span>
        );
    }

    const valueRoundedToPlace = Math.floor(value / place);
    const animatedValue = useSpring(valueRoundedToPlace, { bounce: 0, duration: 800 }); // Customize spring for smoothness

    useEffect(() => {
        animatedValue.set(valueRoundedToPlace);
    }, [animatedValue, valueRoundedToPlace]);

    return (
        <span className={`relative w-[0.6em] tabular-nums inline-block overflow-hidden ${className || ''}`} style={{ height, ...digitStyle }}>
            {Array.from({ length: 10 }, (_, i) => (
                <Number key={i} mv={animatedValue} number={i} height={height} color={color} className={className} />
            ))}
        </span>
    );
}

interface CounterProps {
    value: number;
    fontSize?: number;
    padding?: number;
    currentValue?: number; // allow manual override if needed, but 'value' is primary
    places?: PlaceValue[];
    gap?: number;
    borderRadius?: number;
    horizontalPadding?: number;
    textColor?: string;
    fontWeight?: React.CSSProperties['fontWeight'];
    containerStyle?: React.CSSProperties;
    counterStyle?: React.CSSProperties;
    digitStyle?: React.CSSProperties;
    gradientHeight?: number;
    gradientFrom?: string;
    gradientTo?: string;
    topGradientStyle?: React.CSSProperties;
    bottomGradientStyle?: React.CSSProperties;
    className?: string; // Add className prop
    includeDecimals?: boolean;
    minDigits?: number; // Pad with leading zeros if needed
}

export default function AnimatedCounter({
    value,
    fontSize = 24, // Default font size
    padding = 0,
    places,
    gap = 0, // Reduced gap default
    borderRadius = 4,
    horizontalPadding = 0,
    textColor = 'inherit',
    fontWeight = 'inherit',
    containerStyle,
    counterStyle,
    digitStyle,
    gradientHeight = 0, // Disable gradient by default
    gradientFrom,
    gradientTo,
    topGradientStyle,
    bottomGradientStyle,
    className,
    minDigits = 1,
}: CounterProps) {
    const height = fontSize + padding;

    // Calculate places if not provided
    const placesToUse = places ?? (() => {
        const valStr = Math.round(value).toString();
        const len = Math.max(valStr.length, minDigits);
        const result: PlaceValue[] = [];
        for (let i = len - 1; i >= 0; i--) {
            result.push(10 ** i);
        }
        return result;
    })();


    const defaultCounterStyle: React.CSSProperties = {
        fontSize,
        gap,
        borderRadius,
        paddingLeft: horizontalPadding,
        paddingRight: horizontalPadding,
        color: textColor,
        fontWeight,
        lineHeight: 1, // Important for height calculation
    };

    return (
        <span className={`relative inline-block ${className || ''}`} style={containerStyle}>
            <span className="flex overflow-hidden leading-none" style={{ ...defaultCounterStyle, ...counterStyle }}>
                {placesToUse.map(place => (
                    <Digit key={place} place={place} value={value} height={height} digitStyle={digitStyle} color={textColor} className={className} />
                ))}
            </span>
        </span>
    );
}
